/**
 * The Nova mascot's motion, shared by the Skia renderer (`NovaMascot.tsx`)
 * and the SVG one for web (`NovaMascot.web.tsx`) so both move identically.
 * Every function is a worklet: it runs per frame on the UI thread. Units are
 * the mascot's 250-unit box (see `nova-mascot-geometry.ts`).
 */
import { BLINK, BUBBLE, MASCOT_EYE, MASCOT_EYES, MASCOT_FOOT_Y, RING, RING_DOTS } from './nova-mascot-geometry';

export const TAU = Math.PI * 2;

/** 0..1 weight of each state; idle is what's left over. */
export type MascotBlends = {
  listening: number;
  thinking: number;
  speaking: number;
  error: number;
};

export function mix(a: number, b: number, k: number): number {
  'worklet';
  return a + (b - a) * k;
}

/** Openness of the lids at time t: a quick close (45%) and a slower open (55%). */
export function blinkOpenness(t: number): number {
  'worklet';
  const k = Math.floor(t / BLINK.cycle);
  const p = t - k * BLINK.cycle;
  let x = -1;
  if (p < BLINK.duration) x = p / BLINK.duration;
  else if (k % 3 === 2 && p >= BLINK.second && p < BLINK.second + BLINK.duration) {
    x = (p - BLINK.second) / BLINK.duration;
  }
  if (x < 0) return 1;
  const shut = 1 - BLINK.minOpen;
  return x < 0.45 ? 1 - (x / 0.45) * shut : BLINK.minOpen + ((x - 0.45) / 0.55) * shut;
}

/**
 * Body placement: a breath and a bob at rest, a squash with the mic, a
 * bounce with Nova's own voice, a shrink into the thinking bubble. Scale
 * pivots on the feet, so squash lands on the ground.
 */
export function bodyPose(
  t: number,
  b: MascotBlends,
  level: number,
  shake: number,
): { tx: number; ty: number; sx: number; sy: number; opacity: number } {
  'worklet';
  const lvl = Math.min(1, level);
  const talk = lvl * b.speaking;
  const hear = lvl * b.listening;
  const breath = 1 + Math.sin((t / 3.4) * TAU) * 0.012;
  const bob = Math.sin((t * TAU) / 2.4) * 2.4 * (1 - b.error) * (1 - b.speaking * 0.6);
  const sx = breath * (1 - talk * 0.035 + hear * 0.02) * (1 - b.thinking * 0.12);
  const sy = breath * (1 + talk * 0.07 - hear * 0.015 - b.error * 0.03) * (1 - b.thinking * 0.12);
  const driftX = Math.sin((t * TAU) / 7.9 + 1.9) * 1.2;
  return {
    tx: driftX + shake,
    ty: bob - talk * 5 + MASCOT_FOOT_Y * (1 - sy),
    sx,
    sy,
    opacity: 1 - b.thinking,
  };
}

/**
 * One eye: slow wander and blinks at rest, an upward capsule while
 * listening, a squint with each syllable while speaking, downcast on error.
 * `ry` makes the rounded rect an ellipse (h/2) or a capsule (w/2).
 */
export function eyePose(
  which: 0 | 1,
  t: number,
  b: MascotBlends,
  level: number,
): { x: number; y: number; w: number; h: number; rx: number; ry: number } {
  'worklet';
  const { cx, cy } = MASCOT_EYES[which];
  const calm = 1 - Math.max(b.listening, b.error);
  const wanderX = (0.55 * Math.sin((t * TAU) / 11.3 + 0.4) + 0.3 * Math.sin((t * TAU) / 3.7 + 2.1)) * 4.2;
  const wanderY = Math.sin((t * TAU) / 9.1 + 1.3) * 2.6;
  const tx = wanderX * calm;
  const ty = wanderY * calm - b.listening * 4 + b.error * 6;
  const w = MASCOT_EYE.w * (1 + b.listening * 0.06);
  const h =
    MASCOT_EYE.h *
    (1 - b.listening * 0.22) *
    (1 - b.speaking * Math.min(1, level) * 0.24) *
    (1 - b.error * 0.3) *
    blinkOpenness(t);
  const ry = Math.min(h / 2, mix(h / 2, w / 2, b.listening));
  return { x: cx + tx - w / 2, y: cy + ty - h / 2, w, h, rx: w / 2, ry };
}

/** The reference's wave speed: 0.58 turns a second, a touch quicker when loud. */
export function ringWaveSpeed(level: number): number {
  'worklet';
  return 0.58 + Math.min(1, level) * 0.18;
}

/**
 * Dot `index` of the listening ring: a travelling wave that swells with the
 * mic. `phase` is the wave's accumulated position in turns (advanced a frame
 * at a time by `ringWaveSpeed`), never time × speed: multiplying the whole
 * elapsed time by a speed that follows the voice made the wave leap on every
 * change in loudness, which read as the ring spinning far too fast.
 */
export function ringDot(
  index: number,
  phase: number,
  level: number,
  appear: number,
): { cx: number; cy: number; r: number; opacity: number } {
  'worklet';
  const angle = (index / RING_DOTS) * TAU;
  const lvl = Math.min(1, level) * appear;
  const s = Math.sin(angle);
  const wave = Math.sin(angle * 3 - phase * TAU);
  const depth = (s + 1) / 2;
  return {
    cx: Math.cos(angle) * RING.rx * (0.9 + 0.1 * appear),
    cy: RING.cy + s * RING.ry + wave * RING.wave * (1 + lvl * 2.4),
    r: (RING.dot + Math.abs(wave) * 0.8 + lvl * 1.8) * (0.78 + 0.3 * depth) * appear,
    opacity: Math.min(1, (0.62 + Math.abs(wave) * 0.2 + lvl * 0.15) * (0.42 + 0.58 * depth) * appear),
  };
}

/** The far half of the ring passes behind the body. Fixed per dot. */
export function isFarDot(index: number): boolean {
  'worklet';
  return Math.sin((index / RING_DOTS) * TAU) < 0;
}

/** A 1.5s wave across the bubble's three dots, each a third of a turn behind. */
export function bubbleDot(index: number, t: number): { cy: number; r: number; opacity: number } {
  'worklet';
  const k = 0.5 + 0.5 * Math.sin((t * TAU) / 1.5 - (index * TAU) / 3);
  return { cy: BUBBLE.y - k * 3.5, r: BUBBLE.dotR * (1 + k * 0.35), opacity: 0.58 + k * 0.42 };
}

/** The ground shadow narrows as Nova rises and while she's a bubble. */
export function groundScale(t: number, b: MascotBlends, level: number): number {
  'worklet';
  const lift = Math.sin((t * TAU) / 2.4) * (1 - b.error);
  const talk = Math.min(1, level) * b.speaking;
  return (1 - lift * 0.03 - talk * 0.06) * (1 - b.thinking * 0.18);
}
